import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Sparkles, Users, Building2, MessageCircle, Filter } from "lucide-react";
import { api } from "@/lib/api";
import CtaBanner from "@/components/CtaBanner";
import InlineCTA from "@/components/InlineCTA";
import { usePageContent, useSetting } from "@/context/CmsContext";
import { useLocale } from "@/context/LocaleContext";
import useSeo from "@/lib/useSeo";

const CATEGORIES = [
    { id: "all",       label_pt: "Todas",           label_en: "All",              emoji: "🎯" },
    { id: "bebidas",   label_pt: "Bebidas & Bar",   label_en: "Drinks & Bar",     emoji: "🍺" },
    { id: "cafe",      label_pt: "Café & Padaria",  label_en: "Coffee & Bakery",  emoji: "☕" },
    { id: "gelados",   label_pt: "Gelados & Doces", label_en: "Ice cream & Sweets", emoji: "🍦" },
    { id: "comida",    label_pt: "Comida",          label_en: "Food",             emoji: "🍣" },
    { id: "estrutura", label_pt: "Estrutura base",  label_en: "Base structure",   emoji: "🛒" },
];

const TRUST_LOGOS = [
    { name: "Heineken", logo: "/brand/logos/heineken.svg" },
    { name: "Red Bull", logo: "/brand/logos/redbull.svg" },
    { name: "Coca-Cola", logo: "/brand/logos/cocacola.svg" },
    { name: "Padaria Portuguesa", logo: null },
    { name: "Illy", logo: null },
    { name: "Compal", logo: "/brand/logos/compal.svg" },
    { name: "Delta Cafés", logo: "/brand/logos/delta.svg" },
    { name: "Super Bock", logo: "/brand/logos/superbock.svg" },
    { name: "EDP", logo: "/brand/logos/edp.svg" },
    { name: "Vodafone", logo: "/brand/logos/vodafone.svg" },
    { name: "NOS", logo: "/brand/logos/nos.svg" },
    { name: "TAP", logo: "/brand/logos/tap.svg" },
];

export default function Eventos() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState("all");
    const [counts, setCounts] = useState({});
    const { t } = usePageContent("eventos");
    const { tr } = useLocale();
    const whatsappNumber = useSetting("whatsapp_number", "351900000000");

    useSeo({
        title: tr("Eventos & Marcas — Casos NOVIFOOD", "Events & Brands — NOVIFOOD cases"),
        description: tr(
            "Heineken, Red Bull, Padaria Portuguesa, Coca-Cola: as marcas que escolheram a NOVIFOOD para activações móveis em Portugal. Veja os nossos casos.",
            "Heineken, Red Bull, Padaria Portuguesa, Coca-Cola: the brands that chose NOVIFOOD for mobile activations in Portugal. See our cases."
        ),
        path: "/eventos",
    });

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/events?limit=100").then(r => r.data || []),
            api.get("/events/categories").then(r => r.data?.counts || {}).catch(() => ({})),
        ])
            .then(([evs, cnts]) => {
                if (!alive) return;
                setEvents(evs);
                setCounts(cnts);
            })
            .catch(() => alive && setEvents([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const filtered = useMemo(() => {
        if (activeCat === "all") return events;
        return events.filter(e => e.category === activeCat);
    }, [events, activeCat]);

    const featured = filtered.filter((e) => e.featured);
    const others = filtered.filter((e) => !e.featured);
    const total = events.length;

    return (
        <>
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border gradient-navy" data-testid="eventos-hero">
                <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />
                <div aria-hidden className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }} />
                <div className="container-x relative hero-y text-white">
                    <span className="inline-flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.28em] text-secondary">
                        <span aria-hidden className="inline-block h-[1.5px] w-6 bg-secondary" />
                        {t("hero", "eyebrow", tr("Casos & Ativações", "Cases & Activations"))}
                    </span>
                    <h1 className="mt-3 max-w-4xl display-xl">
                        {tr("Marcas que ", "Brands ")}<span className="serif-accent text-secondary">{tr("rodam", "rolling")}</span>{tr(" em NOVIFOOD.", " with NOVIFOOD.")}
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/80 leading-relaxed">
                        {t("hero", "subtitle", tr(
                            "Ativações de marca, eventos e franchises que escolheram a NOVIFOOD.",
                            "Brand activations, events and franchises that chose NOVIFOOD."
                        ))}
                    </p>
                </div>
            </section>

            {/* CATEGORY FILTERS */}
            <section
                data-testid="eventos-filters"
                className="sticky top-16 z-30 border-b border-border bg-white/95 backdrop-blur-md"
            >
                <div className="container-x py-4">
                    <div className="flex items-center gap-3 mb-2 sm:mb-0 sm:hidden">
                        <Filter className="h-4 w-4 text-primary" />
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
                            {tr("Filtrar por categoria", "Filter by category")}
                        </span>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                        {CATEGORIES.map((cat) => {
                            const n = cat.id === "all" ? total : (counts[cat.id] || 0);
                            const isActive = activeCat === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => setActiveCat(cat.id)}
                                    data-testid={`eventos-cat-${cat.id}`}
                                    className={
                                        "shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition " +
                                        (isActive
                                            ? "border-primary bg-primary text-white shadow-sm"
                                            : "border-border bg-white text-primary hover:border-primary/40 hover:bg-muted")
                                    }
                                >
                                    <span aria-hidden>{cat.emoji}</span>
                                    <span>{tr(cat.label_pt, cat.label_en)}</span>
                                    <span
                                        className={
                                            "inline-flex items-center justify-center min-w-[22px] px-1.5 h-5 rounded-full text-[10px] font-extrabold " +
                                            (isActive ? "bg-secondary text-primary" : "bg-muted text-primary/70")
                                        }
                                    >
                                        {n}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FEATURED CASES */}
            <section className="container-x section-y" data-testid="eventos-featured">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-xl">
                        <span className="eyebrow">{tr("Em destaque", "Featured")}</span>
                        <h2 className="mt-3 display-lg text-primary">
                            {tr("Histórias com impacto.", "Stories with impact.")}
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-sm">
                        {tr("Activações que mostram a versatilidade da NOVIFOOD em formatos diferentes.", "Activations that show NOVIFOOD's versatility across formats.")}
                    </p>
                </div>

                {loading ? (
                    <div className="mt-14 grid gap-6 lg:grid-cols-2">
                        {[0, 1].map((i) => <div key={i} className="h-[420px] rounded-2xl border border-border bg-white animate-pulse" />)}
                    </div>
                ) : featured.length === 0 ? (
                    <p className="mt-14 text-muted-foreground">
                        {activeCat === "all"
                            ? tr("Em breve, mais casos.", "More cases coming soon.")
                            : tr("Sem destaques nesta categoria.", "No featured cases in this category.")}
                    </p>
                ) : (
                    <div className="mt-14 grid gap-6 lg:grid-cols-2">
                        {featured.map((ev, i) => (
                            <FeaturedCard key={ev.id} ev={ev} index={i} />
                        ))}
                    </div>
                )}

                {/* Inline CTA — nudge to simulador after seeing featured cases */}
                {!loading && featured.length > 0 && (
                    <div className="mt-12">
                        <InlineCTA
                            eyebrow={tr("Inspirado?", "Inspired?")}
                            title={tr("Quero um carrinho como estes — configura o meu.", "I want a cart like these — build mine.")}
                            to="/simulador"
                            label={tr("Abrir simulador", "Open configurator")}
                            variant="gold"
                            icon={Sparkles}
                            testId="eventos-inline-cta-simulador"
                        />
                    </div>
                )}
            </section>

            {/* OTHER CASES GRID */}
            {others.length > 0 && (
                <section className="bg-muted border-y border-border" data-testid="eventos-grid">
                    <div className="container-x section-y">
                        <div className="flex items-end justify-between flex-wrap gap-4">
                            <div className="max-w-2xl">
                                <span className="eyebrow">{tr("Casos de marca", "Brand cases")}</span>
                                <h2 className="mt-3 font-heading text-4xl font-extrabold text-primary leading-tight">
                                    {tr("Mais ativações recentes.", "More recent activations.")}
                                </h2>
                            </div>
                            <Link
                                to="/galeria"
                                data-testid="eventos-see-gallery"
                                className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
                            >
                                {tr("Ver galeria completa", "See full gallery")}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {others.map((ev) => (
                                <article key={ev.id} data-testid={`event-card-${ev.id}`}
                                    className="group rounded-2xl border border-border bg-white overflow-hidden transition hover:-translate-y-1 hover:shadow-xl">
                                    {ev.image_url ? (
                                        <div className="relative aspect-[4/3] overflow-hidden">
                                            <img src={ev.image_url} alt={ev.brand} loading="lazy" className="h-full w-full object-cover transition group-hover:scale-105" />
                                        </div>
                                    ) : (
                                        <div className="aspect-[4/3] bg-muted" />
                                    )}
                                    <div className="p-5">
                                        <div className="font-heading text-xl font-bold text-primary">{ev.brand}</div>
                                        {ev.title && <div className="text-sm text-muted-foreground">{ev.title}</div>}
                                        <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                                            {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>}
                                            {ev.year && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {ev.year}</span>}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* TRUST LOGOS */}
            <section className="container-x section-y" data-testid="eventos-trust">
                <div className="text-center max-w-2xl mx-auto">
                    <Building2 className="h-7 w-7 text-secondary mx-auto" />
                    <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-primary">
                        {t("trust", "title", tr("Algumas das marcas que confiam em nós.", "Some of the brands that trust us."))}
                    </h2>
                    <p className="mt-3 text-muted-foreground">{t("trust", "subtitle", tr("Empresas, agências de eventos e franchises por toda a Europa.", "Companies, event agencies and franchises across Europe."))}</p>
                </div>
                <div className="mt-12 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                    {TRUST_LOGOS.map((brand) => (
                        <div key={brand.name} data-testid={`trust-logo-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="group aspect-[3/2] flex items-center justify-center rounded-xl border border-border bg-white px-5 text-center transition hover:border-secondary/40 hover:shadow-sm">
                            {brand.logo ? (
                                <img
                                    src={brand.logo}
                                    alt={brand.name}
                                    loading="lazy"
                                    className="max-h-10 max-w-[80%] object-contain grayscale opacity-60 transition duration-300 group-hover:grayscale-0 group-hover:opacity-100"
                                />
                            ) : (
                                <span className="font-heading font-bold text-primary/50 text-sm tracking-tight transition group-hover:text-primary/80">{brand.name}</span>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="container-x pb-20" data-testid="eventos-cta">
                <CtaBanner
                    eyebrow={tr("Ative a sua marca", "Activate your brand")}
                    title={tr("Quer a sua marca em destaque aqui?", "Want your brand featured here?")}
                    description={tr(
                        "Criamos ativações chave-na-mão para festivais, lançamentos e eventos corporativos. Envie-nos o briefing e respondemos em 48h com proposta detalhada.",
                        "We create turnkey activations for festivals, launches and corporate events. Send us the briefing and we reply within 48h with a detailed proposal."
                    )}
                    primary={{ to: "/contacto", label: tr("Pedir orçamento", "Request a quote") }}
                    secondary={{ to: `https://wa.me/${whatsappNumber}`, label: tr("Falar no WhatsApp", "Chat on WhatsApp"), external: true, icon: MessageCircle }}
                    variant="navy"
                    testId="eventos-cta-banner"
                />
            </section>
        </>
    );
}

function Stat({ value, label }) {
    return (
        <div>
            <div className="font-heading text-3xl lg:text-4xl font-extrabold text-secondary">{value}</div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white/60">{label}</div>
        </div>
    );
}

function FeaturedCard({ ev, index }) {
    const { tr } = useLocale();
    const reverse = index % 2 === 1;
    return (
        <article data-testid={`featured-event-${ev.id}`}
            className="group rounded-2xl border border-border bg-white overflow-hidden flex flex-col transition hover:-translate-y-1 hover:shadow-2xl">
            {ev.image_url && (
                <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={ev.image_url} alt={ev.brand} className={`h-full w-full object-cover transition group-hover:scale-105 ${reverse ? "" : ""}`} />
                    <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="rounded-full bg-secondary px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-primary">{tr("Em destaque", "Featured")}</span>
                    </div>
                </div>
            )}
            <div className="p-5 sm:p-7 flex-1 flex flex-col">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-heading text-2xl font-extrabold text-primary">{ev.brand}</span>
                    {ev.year && <span className="text-xs uppercase tracking-wider font-bold rounded-full bg-muted px-3 py-1 text-primary/70">{ev.year}</span>}
                </div>
                {ev.title && <div className="mt-1 text-secondary font-bold text-sm uppercase tracking-wider">{ev.title}</div>}
                {ev.description && <p className="mt-4 text-muted-foreground leading-relaxed">{ev.description}</p>}
                <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
                    {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {ev.location}</span>}
                    <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {tr("Activação chave-na-mão", "Turnkey activation")}</span>
                </div>
            </div>
        </article>
    );
}
